import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useDraggable from '../hooks/useDraggable';
import { Compass, ChevronLeft, ChevronRight, X, MessageCircle, Flag } from 'lucide-react';

// A gentle, non-blocking "paseo" through the helpdesk for new agents. It walks the
// user from module to module (actually navigating so they SEE the real thing) with
// a floating coachmark that explains how we work. It never gates interaction — the
// user can click through and use the app while the tour rides along.
const TOUR_STEPS = [
  { key: 'welcome', path: '/' },
  { key: 'tickets', path: '/tickets' },
  { key: 'whatsapp', path: null },
  { key: 'calendar', path: '/calendar' },
  { key: 'errands', path: '/field-tasks' },
  { key: 'vault', path: '/vault' },
  { key: 'kb', path: '/kb' },
  { key: 'assets', path: '/assets' },
  { key: 'chat', path: '/chat' },
  { key: 'finish', path: null },
];

const tourSeenKey = (uid, prefix) => `feature_seen_v1__${prefix}__${uid || 'anon'}`;
const tourStepKey = (uid, prefix) => `${prefix}_step_v1__${uid || 'anon'}`;

// prefix defaults to the internal agent paseo so existing callers keep working.
export function hasSeenTour(uid, prefix = 'agent_paseo') {
  try { return !!localStorage.getItem(tourSeenKey(uid, prefix)); } catch { return false; }
}

// Parametrized so the client portal can pass its own steps/copy while the internal
// agent tour keeps its defaults. `steps` = [{ key, path }], `i18nPrefix` picks the
// t('<prefix>.steps.<key>.{title,body}') namespace, `storagePrefix` scopes the
// "seen"/"current step" localStorage keys.
export default function GuidedTour({ onClose, steps = TOUR_STEPS, i18nPrefix = 'tour', storagePrefix = 'agent_paseo' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.id;
  const STEPS = steps;
  const { pos: tourPos, elementRef: tourRef } =
    useDraggable('guided-tour', { defaultZone: 'bottom-right', width: 360, height: 220, draggable: false });

  const [idx, setIdx] = useState(() => {
    try { return Math.min(parseInt(localStorage.getItem(tourStepKey(uid, storagePrefix)) || '0', 10) || 0, STEPS.length - 1); }
    catch { return 0; }
  });

  const step = STEPS[idx];

  // Navigate to the step's page so the user sees the real module.
  useEffect(() => {
    if (step.path) navigate(step.path);
    try { localStorage.setItem(tourStepKey(uid, storagePrefix), String(idx)); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const finish = useCallback(() => {
    try {
      localStorage.setItem(tourSeenKey(uid, storagePrefix), Date.now().toString());
      localStorage.removeItem(tourStepKey(uid, storagePrefix));
    } catch { /* ignore */ }
    onClose?.();
  }, [uid, onClose, storagePrefix]);

  const isFirst = idx === 0;
  const isLast = idx === STEPS.length - 1;

  const card = (
    <div ref={tourRef} style={{
      // En el dock (sin arrastre): las burbujas se apilan arriba de la tarjeta en vez de
      // quedar tapadas, y en el celular no cae encima de la barra inferior.
      position: 'fixed', left: tourPos.x, top: tourPos.y, zIndex: 1100, width: 360, maxWidth: 'calc(100vw - 24px)',
      transition: 'left 0.18s ease, top 0.18s ease',
    }}>
      <div className="card" style={{
        margin: 0, padding: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        border: '1px solid var(--c-brand)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Compass size={18} style={{ color: 'var(--c-brand)' }} />
          <strong style={{ fontSize: 14 }}>{t(`${i18nPrefix}.steps.${step.key}.title`)}</strong>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--c-text-muted)' }}>
            {idx + 1}/{STEPS.length}
          </span>
          <button className="btn btn-ghost btn-icon" style={{ padding: 2 }} onClick={finish} title={t('tour.exit')}>
            <X size={16} />
          </button>
        </div>

        {step.key === 'whatsapp' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--c-brand)', marginBottom: 6 }}>
            <MessageCircle size={14} /> {t('tour.lookBubble')}
          </div>
        )}

        <p style={{ fontSize: 13, color: 'var(--c-text)', lineHeight: 1.5, margin: '0 0 12px' }}>
          {t(`${i18nPrefix}.steps.${step.key}.body`)}
        </p>

        {/* progress dots */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
          {STEPS.map((s, i) => (
            <div key={s.key} style={{
              height: 4, flex: 1, borderRadius: 2,
              background: i <= idx ? 'var(--c-brand)' : 'var(--c-border)',
            }} />
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button className="btn btn-ghost btn-sm" onClick={finish}>{t('tour.skip')}</button>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {!isFirst && (
              <button className="btn btn-secondary btn-sm" onClick={() => setIdx((i) => i - 1)}>
                <ChevronLeft size={14} /> {t('tour.prev')}
              </button>
            )}
            {!isLast ? (
              <button className="btn btn-primary btn-sm" onClick={() => setIdx((i) => i + 1)}>
                {t('tour.next')} <ChevronRight size={14} />
              </button>
            ) : (
              <button className="btn btn-success btn-sm" onClick={finish}>
                <Flag size={14} /> {t('tour.finish')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(card, document.body);
}
